/**
 * TypeScript types for Procore BIM Search DB SearchPredicate model.
 *
 * These types mirror the C# SearchPredicate model from the backend.
 * The predicate system supports complex queries with logical operators (AND, OR)
 * and various comparison operations. Negation is supported via the `negated` flag
 * on individual predicates.
 */

/**
 * Supported predicate value types
 *
 * Note: Date objects are automatically serialized to ISO 8601 strings
 * (e.g., "2024-01-01T00:00:00.000Z") when sent to the API via JSON.stringify().
 * The API receives them as strings.
 */
export type PredicateValue = string | number | boolean | Date;

/**
 * Predicate operator discriminator (API uses UPPERCASE)
 */
export type PredicateOperator =
    | "EQUAL"
    | "CONTAINS"
    | "STARTS_WITH"
    | "ENDS_WITH"
    | "GREATER_THAN"
    | "LESS_THAN"
    | "GREATER_THAN_OR_EQUAL"
    | "LESS_THAN_OR_EQUAL"
    | "DEFINED"
    | "IN"
    | "RANGE"
    | "AND"
    | "OR"
    | "SEARCH";

/**
 * Base predicate interface
 */
export interface BaseSearchPredicate {
    operator: PredicateOperator;
    negated?: boolean;
}

/**
 * Property-based predicate (has a property field)
 */
export interface PropertyPredicate extends BaseSearchPredicate {
    property: string;
}

/**
 * Equal predicate - exact match
 */
export interface EqualPredicate extends PropertyPredicate {
    operator: "EQUAL";
    value: PredicateValue;
    case_sensitive?: boolean;
}

/**
 * Contains predicate - substring match
 *
 * Note: The case_sensitive flag may be ignored by the backend API,
 * which appears to perform case-insensitive matching by default.
 */
export interface ContainsPredicate extends PropertyPredicate {
    operator: "CONTAINS";
    value: string;
    case_sensitive?: boolean;
}

/**
 * StartsWith predicate - prefix match
 */
export interface StartsWithPredicate extends PropertyPredicate {
    operator: "STARTS_WITH";
    value: string;
    case_sensitive?: boolean;
}

/**
 * EndsWith predicate - suffix match
 */
export interface EndsWithPredicate extends PropertyPredicate {
    operator: "ENDS_WITH";
    value: string;
    case_sensitive?: boolean;
}

/**
 * GreaterThan predicate
 */
export interface GreaterThanPredicate extends PropertyPredicate {
    operator: "GREATER_THAN";
    value: PredicateValue;
}

/**
 * LessThan predicate
 */
export interface LessThanPredicate extends PropertyPredicate {
    operator: "LESS_THAN";
    value: PredicateValue;
}

/**
 * GreaterThanOrEqual predicate
 */
export interface GreaterThanOrEqualPredicate extends PropertyPredicate {
    operator: "GREATER_THAN_OR_EQUAL";
    value: PredicateValue;
}

/**
 * LessThanOrEqual predicate
 */
export interface LessThanOrEqualPredicate extends PropertyPredicate {
    operator: "LESS_THAN_OR_EQUAL";
    value: PredicateValue;
}

/**
 * Defined predicate - checks if property exists
 */
export interface DefinedPredicate extends PropertyPredicate {
    operator: "DEFINED";
}

/**
 * In predicate - value matches any in a set
 */
export interface InPredicate extends PropertyPredicate {
    operator: "IN";
    values: PredicateValue[];
}

/**
 * Range predicate - value within range (inclusive)
 */
export interface RangePredicate extends PropertyPredicate {
    operator: "RANGE";
    min: PredicateValue;
    max: PredicateValue;
}

/**
 * And predicate - logical AND of child predicates
 */
export interface AndPredicate extends BaseSearchPredicate {
    operator: "AND";
    predicates: SearchPredicate[];
}

/**
 * Or predicate - logical OR of child predicates
 */
export interface OrPredicate extends BaseSearchPredicate {
    operator: "OR";
    predicates: SearchPredicate[];
}

/**
 * Search predicate - full text search
 */
export interface SearchTextPredicate extends BaseSearchPredicate {
    operator: "SEARCH";
    value: string;
}

/**
 * Union type of all predicates
 */
export type SearchPredicate =
    | EqualPredicate
    | ContainsPredicate
    | StartsWithPredicate
    | EndsWithPredicate
    | GreaterThanPredicate
    | LessThanPredicate
    | GreaterThanOrEqualPredicate
    | LessThanOrEqualPredicate
    | DefinedPredicate
    | InPredicate
    | RangePredicate
    | AndPredicate
    | OrPredicate
    | SearchTextPredicate;

/**
 * Factory functions for creating predicates
 */

export function equal(property: string, value: PredicateValue, case_sensitive = false): EqualPredicate {
    return { operator: "EQUAL", property, value, case_sensitive };
}

export function contains(property: string, value: string, case_sensitive = false): ContainsPredicate {
    return { operator: "CONTAINS", property, value, case_sensitive };
}

export function startsWith(property: string, value: string, case_sensitive = false): StartsWithPredicate {
    return { operator: "STARTS_WITH", property, value, case_sensitive };
}

export function endsWith(property: string, value: string, case_sensitive = false): EndsWithPredicate {
    return { operator: "ENDS_WITH", property, value, case_sensitive };
}

export function greaterThan(property: string, value: PredicateValue): GreaterThanPredicate {
    return { operator: "GREATER_THAN", property, value };
}

export function lessThan(property: string, value: PredicateValue): LessThanPredicate {
    return { operator: "LESS_THAN", property, value };
}

export function greaterThanOrEqual(property: string, value: PredicateValue): GreaterThanOrEqualPredicate {
    return { operator: "GREATER_THAN_OR_EQUAL", property, value };
}

export function lessThanOrEqual(property: string, value: PredicateValue): LessThanOrEqualPredicate {
    return { operator: "LESS_THAN_OR_EQUAL", property, value };
}

export function defined(property: string): DefinedPredicate {
    return { operator: "DEFINED", property };
}

export function inPredicate(property: string, values: PredicateValue[]): InPredicate {
    return { operator: "IN", property, values };
}

export function range(property: string, min: PredicateValue, max: PredicateValue): RangePredicate {
    return { operator: "RANGE", property, min, max };
}

export function and(...predicates: SearchPredicate[]): AndPredicate | SearchPredicate {
    if (predicates.length === 1) {
        return predicates[0];
    }
    return { operator: "AND", predicates };
}

export function or(...predicates: SearchPredicate[]): OrPredicate | SearchPredicate {
    if (predicates.length === 1) {
        return predicates[0];
    }
    return { operator: "OR", predicates };
}

export function search(value: string): SearchTextPredicate {
    return { operator: "SEARCH", value };
}

export function not<T extends SearchPredicate>(predicate: T): T {
    return { ...predicate, negated: true };
}
